<template>
    <div>
        <v-card id="details" :loading="loading" height="100vh">
            <v-card-title ref="title">
                <v-btn small icon @click="close" color="primary" class="mr-2">
                    <v-icon>
                        mdi-close
                    </v-icon>
                </v-btn>
                <h3>
                    {{
                        !originalOrder
                            ? $vuetify.breakpoint.smAndDown
                                ? 'New PL'
                                : 'New Packing List'
                            : $vuetify.breakpoint.smAndDown
                            ? 'Edit PL'
                            : `Edit Packing List (${originalOrder.code})`
                    }}
                    - {{ project.name }}
                </h3>
                <v-btn
                    v-if="packingList.rejectionReasons"
                    small
                    icon
                    @click="rejectionTable = true"
                    color="red"
                    class="ml-2"
                >
                    <v-icon>
                        mdi-book-cancel-outline
                    </v-icon>
                </v-btn>
                <v-btn
                    v-if="canToggleImportant"
                    small
                    icon
                    @click="toggleImportant"
                    :color="packingList.important ? 'red' : 'grey'"
                    class="ml-2"
                    :loading="loadingImportant"
                >
                    <v-icon>
                        {{
                            packingList.important
                                ? 'mdi-flag'
                                : 'mdi-flag-outline'
                        }}
                    </v-icon>
                </v-btn>
                <v-icon
                    v-else-if="packingList.important"
                    color="red"
                    class="ml-2"
                >
                    mdi-flag
                </v-icon>
                <v-spacer />
                <v-btn
                    small
                    icon
                    color="primary"
                    :disabled="loading"
                    @click="generatePackagesXLSX"
                    v-if="packingList.id"
                >
                    <v-icon>mdi-file-excel</v-icon>
                </v-btn>
                <v-btn
                    small
                    icon
                    color="primary"
                    :disabled="loading"
                    @click="selectScopes"
                    v-if="packingList.id"
                >
                    <v-icon>mdi-printer-pos</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider class="ml-4 mr-5" />
            <v-card-text
                class="pt-0"
                id="virtual-scroll-table"
                :style="{ height: '81vh' }"
            >
                <v-form v-model="valid" class="py-2">
                    <v-row no-gutters class="mt-4" ref="projectSelector">
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="6"
                            class="pl-2"
                        >
                            <v-text-field
                                v-model="project.name"
                                prepend-icon="mdi-account-hard-hat"
                                label="Project"
                                readonly
                                hide-details
                            />
                        </v-col>
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="6"
                            class="pl-2"
                        >
                            <v-text-field
                                ref="deliveryInfoInput"
                                v-model="deliveryInfoString"
                                hide-details
                                label="Delivery Information"
                                prepend-icon="mdi-map-marker"
                                :disabled="loading || blockActions"
                                @focus="openDeliveryInfoDialog"
                                @blur="resetFocus"
                                readonly
                            >
                            </v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="6"
                            class="pl-2"
                        >
                            <v-menu
                                ref="menu"
                                v-model="menu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="
                                            packingList.requestedShipmentDate
                                        "
                                        label="Requested Date For Shipment"
                                        prepend-icon="mdi-calendar-blank"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        hide-details
                                        :disabled="loading || blockActions"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="packingList.requestedShipmentDate"
                                    @input="
                                        updatePackingList({
                                            requestedShipmentDate:
                                                packingList.requestedShipmentDate,
                                        })
                                    "
                                    :min="
                                        !createPackingListPermission
                                            ? minDate
                                            : undefined
                                    "
                                >
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="6"
                            class="pl-2"
                        >
                            <v-menu
                                ref="menu2"
                                v-model="menu2"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                                :disabled="
                                    (!schedulingOptions && !isProjectManager) ||
                                        loading
                                "
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="packingList.scheduledFor"
                                        label="Scheduled For"
                                        prepend-icon="mdi-calendar-clock"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        hide-details
                                        :disabled="
                                            (!schedulingOptions &&
                                                !isProjectManager) ||
                                                loading ||
                                                blockActions
                                        "
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    v-model="packingList.scheduledFor"
                                    @input="
                                        updatePackingList({
                                            scheduledFor:
                                                packingList.scheduledFor,
                                        })
                                    "
                                    :min="
                                        !createPackingListPermission
                                            ? minDate
                                            : undefined
                                    "
                                >
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="mt-0">
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="6"
                            class="pl-2"
                        >
                            <v-text-field
                                v-model="packingList.notes"
                                rows="1"
                                hide-details
                                label="Notes"
                                prepend-icon="mdi-text"
                                :disabled="loading || blockActions"
                                @blur="
                                    updatePackingList({
                                        notes: packingList.notes,
                                    })
                                "
                            >
                            </v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            xl="3"
                            lg="3"
                            md="3"
                            sm="6"
                            class="pl-2"
                        >
                            <v-text-field
                                v-model="packingList.container"
                                rows="1"
                                hide-details
                                label="Container"
                                prepend-icon="mdi-package"
                                @blur="
                                    updatePackingList({
                                        container: packingList.container,
                                    })
                                "
                                :disabled="
                                    (!createPackingListPermission &&
                                        !schedulingOptions) ||
                                        blockActions
                                "
                            >
                                <template v-slot:append>
                                    <v-tooltip bottom>
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-btn
                                                icon
                                                @click="generateTempContainer"
                                                small
                                                tabindex="-1"
                                                v-bind="attrs"
                                                v-on="on"
                                                :disabled="
                                                    !schedulingOptions ||
                                                        loading
                                                "
                                            >
                                                <v-icon color="primary"
                                                    >mdi-reload</v-icon
                                                >
                                            </v-btn>
                                        </template>
                                        <span
                                            >Generate temporal random
                                            container</span
                                        >
                                    </v-tooltip>
                                </template>
                            </v-text-field>
                        </v-col>
                        <v-col
                            cols="12"
                            xl="6"
                            lg="6"
                            md="6"
                            sm="6"
                            class="pl-2"
                            v-if="createdBy"
                        >
                            <v-text-field
                                v-model="createdBy"
                                prepend-icon="mdi-account"
                                label="Created By"
                                readonly
                            />
                        </v-col>
                        <v-row
                            cols="12"
                            xl="6"
                            lg="6"
                            md="6"
                            sm="6"
                            class="py-0"
                        >
                            <v-col
                                cols="12"
                                xl="6"
                                lg="6"
                                md="6"
                                sm="6"
                                class="py-3"
                            >
                                <v-combobox
                                    v-model="packingList.selectedWorkOrders"
                                    :items="projectWorkOrders"
                                    item-text="code"
                                    label="WorkOrders"
                                    prepend-icon="mdi-hammer-wrench"
                                    multiple
                                    chips
                                    small-chips
                                    :readonly="
                                        !schedulingOptions &&
                                            (!packingList.packages ||
                                                packingList.packages.length ===
                                                    0)
                                    "
                                    hide-details
                                    @change="
                                        updatePackingList({
                                            selectedWorkOrders:
                                                packingList.selectedWorkOrders,
                                        })
                                    "
                                    :disabled="loading || blockActions"
                                ></v-combobox>
                            </v-col>
                            <v-col
                                cols="12"
                                xl="6"
                                lg="6"
                                md="6"
                                sm="6"
                                class="py-3"
                            >
                                <v-combobox
                                    v-model="packingList.selectedBOMs"
                                    :items="projectBOMs"
                                    item-text="code"
                                    label="BOMs"
                                    prepend-icon="mdi-list-box-outline"
                                    multiple
                                    chips
                                    small-chips
                                    readonly
                                    hide-details
                                    :disabled="loading || blockActions"
                                ></v-combobox>
                            </v-col>
                        </v-row>
                    </v-row>
                </v-form>
                <v-row ref="files" class="px-3 pt-5">
                    <v-col cols="12" xl="2" lg="2" md="2" class="pl-2">
                        <v-row no-gutters class="d-flex align-center mt-0 ml-1">
                            <v-simple-checkbox
                                color="primary"
                                v-model="packingList.airShipment"
                                :disabled="
                                    loading ||
                                        ![
                                            'inProgress',
                                            'reserved',
                                            'reservedForProject',
                                            'domesticShipping',
                                        ].includes(packingList.status) ||
                                        blockActions
                                "
                                @click="
                                    updatePackingList({
                                        airShipment: packingList.airShipment,
                                    })
                                "
                            ></v-simple-checkbox>
                            <p class="ma-0 pa-0">Air Shipment</p>
                        </v-row>
                        <v-row
                            no-gutters
                            class="d-flex align-center mt-0 ml-1"
                            v-if="packingList.id"
                        >
                            <v-simple-checkbox
                                color="primary"
                                v-model="packingList.blockActionsFromPMs"
                                :disabled="loading || !planeationPermission"
                                @input="
                                    updateBlockActionsFromPms({
                                        blockActionsFromPMs:
                                            packingList.blockActionsFromPMs,
                                    })
                                "
                            ></v-simple-checkbox>
                            <p class="ma-0 pa-0">
                                Block outside actions
                            </p>
                        </v-row>
                    </v-col>
                    <v-col cols="12" xl="3" lg="3" class="py-0">
                        <div class="d-flex my-2">
                            <div class="d-flex align-center pr-1">
                                <strong>ATTACHED: </strong>
                            </div>
                            <div class="d-flex align-center flex-wrap">
                                <v-btn
                                    color="grey lighten-3"
                                    elevation="0"
                                    x-small
                                    class="ma-1 text-capitalize grey--text text--darken-1  "
                                    v-for="(file, i) in packingList.attachments"
                                    :key="i"
                                >
                                    <v-icon
                                        class="mr-1"
                                        color="primary"
                                        :style="{ 'font-size': '17px' }"
                                    >
                                        mdi-paperclip
                                    </v-icon>
                                    <span
                                        @click="openFileAttached(file)"
                                        :style="{
                                            'font-size': '11px',
                                            color: '#2b81d6',
                                            'text-decoration': 'underline',
                                            cursor: 'pointer',
                                        }"
                                        >{{ file }}</span
                                    >
                                    <v-icon
                                        :disabled="loading || blockActions"
                                        @click="deleteFile(file)"
                                        class="ml-1"
                                        small
                                    >
                                        mdi-close-circle
                                    </v-icon>
                                </v-btn>
                                <v-btn
                                    @click="onButtonClick"
                                    depressed
                                    fab
                                    x-small
                                    color="primary"
                                    class="ml-xl-2 ml-lg-2 ml-md-2 ml-sm-2"
                                    :disabled="loading || blockActions"
                                >
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                                <input
                                    ref="uploader2"
                                    class="d-none"
                                    type="file"
                                    @change="onFileChanged"
                                />
                            </div>
                        </div>
                    </v-col>
                </v-row>
                <v-row class="py-2 ma-0">
                    <v-spacer />
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                fab
                                small
                                color="primary"
                                dark
                                @click="downloadCurrentPL"
                                class="mx-2"
                                :disabled="
                                    loading ||
                                        packingList?.packages?.length == 0
                                "
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>Download current PL in template</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                rounded
                                color="primary"
                                @click="openImportDialog"
                                :disabled="loading || blockActions"
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon left>mdi-file-import</v-icon>
                                IMPORT
                            </v-btn>
                        </template>
                        <span>Create suggested packages</span>
                    </v-tooltip>
                    <v-btn
                        rounded
                        color="primary"
                        @click="openCreationDialog"
                        class="mx-2"
                        :disabled="loading || blockActions || packageForm"
                    >
                        <v-icon>
                            mdi-plus
                        </v-icon>
                        NEW PACKAGE
                    </v-btn>
                </v-row>
                <v-container fluid px-0>
                    <v-row>
                        <v-col
                            cols="12"
                            v-for="(packageElement,
                            packageIndex) in packingList.packages"
                            :key="packageElement.id || packageIndex"
                            class="mb-5"
                            :class="{
                                'package-row-highlight': isPackageHighlighted(
                                    packageElement.id
                                ),
                            }"
                        >
                            <v-data-table
                                :headers="headers"
                                :items="packageElement.items"
                                class="elevation-0"
                                :mobile-breakpoint="0"
                                disable-pagination
                                hide-default-footer
                                multiple-expand
                                :expanded.sync="packageElement.items"
                            >
                                <template v-slot:top>
                                    <v-row
                                        no-gutter
                                        :style="{
                                            'background-color': '#eeeeee',
                                        }"
                                        class="mt-0 mx-0"
                                    >
                                        <v-col
                                            cols="12"
                                            :style="{
                                                display: 'flex',
                                                flexWrap: $vuetify.breakpoint
                                                    .smAndDown
                                                    ? 'wrap'
                                                    : 'nowrap',
                                            }"
                                        >
                                            <h2
                                                class="mt-1 pl-2"
                                                :style="{
                                                    'min-width': 'fit-content',
                                                }"
                                            >
                                                Package
                                                {{ packageIndex + 1 }}
                                            </h2>
                                            <h4
                                                class="d-flex align-end pl-2 pb-1"
                                                :style="{
                                                    'min-width': 'fit-content',
                                                }"
                                            >
                                                {{
                                                    `(W${
                                                        packageElement
                                                            .packageDimensions
                                                            .width
                                                    } x H${
                                                        packageElement
                                                            .packageDimensions
                                                            .height
                                                    } x L${
                                                        packageElement
                                                            .packageDimensions
                                                            .depth
                                                    } ${
                                                        packageElement
                                                            .packageDimensions
                                                            .units
                                                    }) - Crate Mass: ${
                                                        packageElement.packageDimensions &&
                                                        packageElement
                                                            .packageDimensions
                                                            .mass
                                                            ? Number(
                                                                  packageElement
                                                                      .packageDimensions
                                                                      .mass || 0
                                                              ).toFixed(1)
                                                            : 0
                                                    } Kg `
                                                }}<v-btn
                                                    x-small
                                                    icon
                                                    color="primary"
                                                    class="ml-1"
                                                    v-if="
                                                        project &&
                                                            project.projectManager &&
                                                            (isProjectManager ||
                                                                createPackingListPermission) &&
                                                            ![
                                                                'completed',
                                                                'deleted',
                                                            ].includes(
                                                                packingList.status
                                                            )
                                                    "
                                                    :disabled="
                                                        loading || blockActions
                                                    "
                                                    @click="
                                                        openUpdatePackageParams(
                                                            packageElement
                                                        )
                                                    "
                                                >
                                                    <v-icon
                                                        >mdi-pencil</v-icon
                                                    > </v-btn
                                                >{{
                                                    `-  BOM:${
                                                        packageElement.packageBOM
                                                            ? packageElement.packageBOM
                                                            : ''
                                                    }`
                                                }}
                                            </h4>
                                            <v-spacer />
                                            <v-btn
                                                small
                                                icon
                                                :disabled="loading"
                                                @click="
                                                    printPackage(
                                                        packageElement,
                                                        packageIndex + 1
                                                    )
                                                "
                                            >
                                                <v-icon>mdi-printer-pos</v-icon>
                                            </v-btn>
                                            <v-btn
                                                small
                                                icon
                                                @click="
                                                    openEditDialog(
                                                        packageElement
                                                    )
                                                "
                                                :disabled="
                                                    loading || blockActions
                                                "
                                            >
                                                <v-icon>mdi-pencil</v-icon>
                                            </v-btn>
                                            <v-btn
                                                small
                                                icon
                                                @click="
                                                    openDeleteDialog(
                                                        packageElement
                                                    )
                                                "
                                                :disabled="
                                                    loading || blockActions
                                                "
                                            >
                                                <v-icon>mdi-delete</v-icon>
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </template>
                                <template
                                    v-slot:[`item.finishAndUCCode`]="{ item }"
                                >
                                    <p class="my-0">
                                        <v-tooltip right>
                                            <template v-slot:activator="{ on }">
                                                <span
                                                    v-on="on"
                                                    style="cursor: pointer;"
                                                >
                                                    {{
                                                        replaceItem(
                                                            item,
                                                            'description'
                                                        )
                                                    }}
                                                </span>
                                            </template>
                                            <span>
                                                {{ replaceItem(item, 'code') }}
                                            </span>
                                        </v-tooltip>
                                    </p>
                                </template>
                                <!--FOOTER-->
                                <template v-slot:[`item.mass`]="{ item }">
                                    <p class="my-0">
                                        {{ Number(item.mass || 0).toFixed(1) }}
                                        Kg
                                    </p>
                                </template>
                                <template
                                    v-slot:[`item.packingMass`]="{ item }"
                                >
                                    <p class="my-0">
                                        {{
                                            (
                                                item.mass * item.packingQty
                                            ).toFixed(1)
                                        }}
                                        Kg
                                    </p>
                                </template>
                                <template v-slot:[`item.type`]="{ item }">
                                    <p class="my-0">
                                        {{
                                            item['bom'] != undefined
                                                ? 'BOM'
                                                : 'WorkOrder'
                                        }}
                                    </p>
                                </template>
                                <template
                                    v-slot:[`item.suggestedMass`]="{ item }"
                                >
                                    <p class="my-0">
                                        {{
                                            (
                                                item.mass *
                                                Number(item.suggestedQty)
                                            ).toFixed(1)
                                        }}
                                        Kg
                                    </p>
                                </template>
                                <template
                                    v-slot:[`item.suggestedQty`]="{ item }"
                                >
                                    <p class="my-0 text-right">
                                        {{ item.suggestedQty }}
                                    </p>
                                </template>
                                <template v-slot:[`item.code`]="{ item }">
                                    <p class="my-0">
                                        {{ item.workOrder || item.bom }}
                                        {{
                                            woData[item.workOrder]?.reprocess
                                                ? '(Reprocess)'
                                                : ''
                                        }}
                                    </p>
                                </template>
                                <template v-slot:[`item.partNumber`]="{ item }">
                                    <p class="my-0">
                                        {{ item.partNumber || item.code }}
                                    </p>
                                </template>
                                <template v-slot:[`body.append`]>
                                    <tr
                                        :style="{
                                            'background-color': '#eeeeee',
                                        }"
                                    >
                                        <td
                                            v-for="(header,
                                            colIndex) in headers"
                                            :key="colIndex"
                                            :class="{
                                                'font-weight-bold': [
                                                    'suggestedQty',
                                                    'packingQty',
                                                    'packingMass',
                                                    'suggestedMass',
                                                    'notes',
                                                ].includes(header.value),
                                            }"
                                            :style="
                                                [
                                                    'suggestedQty',
                                                    'packingQty',
                                                    'packingMass',
                                                    'suggestedMass',
                                                    'notes',
                                                ].includes(header.value)
                                                    ? { 'text-align': 'right' }
                                                    : {}
                                            "
                                        >
                                            <template
                                                v-if="
                                                    header.value ===
                                                        'suggestedQty'
                                                "
                                            >
                                                {{
                                                    workOrderTotalSuggested(
                                                        packageElement
                                                    )
                                                }}
                                            </template>
                                            <template
                                                v-else-if="
                                                    header.value ===
                                                        'packingQty'
                                                "
                                            >
                                                {{
                                                    workOrderTotalPacking(
                                                        packageElement
                                                    )
                                                }}
                                            </template>
                                            <template
                                                v-else-if="
                                                    header.value ===
                                                        'packingMass' ||
                                                        header.value ===
                                                            'suggestedMass'
                                                "
                                            >
                                                {{
                                                    workOrderTotalMass(
                                                        packageElement
                                                    ).toFixed(1)
                                                }}
                                                Kg
                                            </template>
                                            <template
                                                v-else-if="
                                                    header.value === 'notes'
                                                "
                                            >
                                                Gross Mass:
                                                {{
                                                    (
                                                        (+packageElement
                                                            .packageDimensions
                                                            ?.mass || 0) +
                                                        +packageElement.items.reduce(
                                                            (acc, item) =>
                                                                acc +
                                                                (item.packingMass ||
                                                                    0),
                                                            0
                                                        )
                                                    ).toFixed(1)
                                                }}
                                                Kg
                                                <v-btn
                                                    x-small
                                                    icon
                                                    color="primary"
                                                    class="ml-1"
                                                    v-if="
                                                        project &&
                                                            project.projectManager &&
                                                            (isProjectManager ||
                                                                createPackingListPermission) &&
                                                            ![
                                                                'completed',
                                                                'deleted',
                                                            ].includes(
                                                                packingList.status
                                                            )
                                                    "
                                                    @click="
                                                        openUpdatePackageGrossMass(
                                                            packageElement
                                                        )
                                                    "
                                                    :disabled="
                                                        loading || blockActions
                                                    "
                                                >
                                                    <v-icon>mdi-pencil</v-icon>
                                                </v-btn>
                                            </template>
                                        </td>
                                    </tr>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-container>
                <v-row
                    no-gutters
                    :style="{ 'background-color': '#eeeeee' }"
                    class="mt-8"
                    ref="totals"
                >
                    <v-col cols="12" xl="1" lg="1" md="1">
                        <p class="my-2 font-weight-black ml-4">TOTALS</p>
                    </v-col>
                    <v-col cols="12" xl="3" lg="3" md="3">
                        <p class="my-2  ml-4 font-weight-black">
                            QUANTITY OF PACKAGES:
                            {{ packingList.packages.length }}
                        </p>
                    </v-col>
                    <v-col cols="12" xl="3" lg="3" md="3">
                        <p class="my-2  ml-4 font-weight-black">
                            QUANTITY OF ITEMS: {{ totalItemsQty() }}
                        </p>
                    </v-col>
                    <v-col cols="12" xl="3" lg="3" md="3">
                        <p class="my-2  ml-4 font-weight-black">
                            NET MASS: {{ netMass().toFixed(1) }} Kg
                        </p>
                    </v-col>
                    <v-col cols="12" xl="2" lg="2" md="2">
                        <p class="my-2  ml-4 font-weight-black">
                            GROSS MASS: {{ grossMass().toFixed(1) }} Kg
                        </p>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions ref="actions" class="my-2 flex-column align-end">
                <div class="d-flex justify-end w-100">
                    <v-btn
                        color="primary"
                        rounded
                        class="mt-n2 mr-3 white--text"
                        :loading="loading"
                        @click="buttonActions()"
                        :disabled="buttonValidations()"
                        v-if="!packingList.id"
                    >
                        CREATE
                    </v-btn>
                    <v-btn
                        color="primary"
                        rounded
                        class="mt-n2 mr-3 white--text"
                        :loading="loading"
                        @click="buttonActions()"
                        v-if="
                            originalOrder &&
                                originalOrder.status &&
                                (originalOrder.status == 'rejected' ||
                                    originalOrder.status == 'inProgress' ||
                                    originalOrder.status == 'reserved' ||
                                    originalOrder.status ==
                                        'reservedForProject' ||
                                    originalOrder.status ==
                                        'domesticShipping') &&
                                originalOrder.code
                        "
                        :disabled="
                            !valid ||
                                packingList.packages.length == 0 ||
                                qtyValidation() ||
                                blockActions ||
                                reservedWorkOrdersMismatch()
                        "
                    >
                        MARK AS PENDING
                    </v-btn>
                </div>
                <p
                    v-if="missingReservedWorkOrders().length > 0"
                    class="mt-2 mr-3 mb-0 error--text caption text-right"
                >
                    Cannot mark as pending: reserved work
                    {{
                        missingReservedWorkOrders().length === 1
                            ? 'order'
                            : 'orders'
                    }}
                    <strong>{{
                        missingReservedWorkOrders().join(', ')
                    }}</strong>
                    {{
                        missingReservedWorkOrders().length === 1 ? 'is' : 'are'
                    }}
                    not included in the selected work orders.
                </p>
            </v-card-actions>
        </v-card>
        <!--PACKAGE FORM-->
        <v-dialog
            v-model="packageForm"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <PackageForm
                v-if="packageForm"
                :projectWorkOrders="projectWorkOrders"
                :BOMs="projectBOMs"
                :measureUnits="measureUnits"
                :editionView="editionView"
                :originalPackage="selectedPackage"
                :packingList="packingList"
                :onlySuggested="onlySuggested"
                @close="closePackageForm"
                @addPackage="addPackage"
                @replacePackage="replacePackage"
                @recalculateBalances="recalculateBalances"
            />
        </v-dialog>
        <!-- IMPORT PACKAGE DIALOG -->
        <v-dialog
            v-model="importDialog"
            :retain-focus="false"
            persistent
            max-width="1200px"
        >
            <ImportPackageDialog
                v-if="importDialog"
                :project="project"
                :packingList="packingList"
                @close="closeImportDialog"
                @import="handleImport"
            />
        </v-dialog>
        <!-- DELETE DIALOG -->
        <v-dialog
            v-model="deleteDialog"
            :retain-focus="false"
            persistent
            max-width="600"
        >
            <v-card :loading="loading">
                <v-card-title class="text-h5">
                    <v-btn
                        small
                        icon
                        @click="closeDeleteDialog"
                        color="primary"
                        class="mr-2"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                    <h3>
                        Delete Package
                        {{ selectedPackage ? selectedPackage.index : '' }}
                    </h3></v-card-title
                >
                <v-divider class="ml-4 mr-5" />
                <v-card-text class="pt-4">
                    Are you sure you want to delete this package?
                </v-card-text>
                <v-spacer></v-spacer>
                <v-card-actions class="pb-4">
                    <v-btn
                        @click="closeDeleteDialog"
                        color="primary"
                        :loading="loading"
                        text
                    >
                        NO
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deletePackage"
                        color="error"
                        :loading="loading"
                        rounded
                    >
                        YES
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--DeliveryInfo-->
        <v-dialog persistent max-width="500px" v-model="deliveryInfoDialog">
            <DeliveryInfoForm
                v-if="deliveryInfoDialog"
                :packingList="packingList"
                :clientId="project.client"
                @replaceDeliveryInfo="setDeliveryInfo"
                @closeDialog="closeDeliveryInfoDialog"
            />
        </v-dialog>
        <!--Rejected-->
        <v-dialog persistent max-width="600px" v-model="rejectionTable">
            <RejectedPackingListTable
                v-if="rejectionTable"
                :items="packingList.rejectionReasons"
                @closeDialog="rejectionTable = false"
            />
        </v-dialog>
        <!--Scopes Select-->
        <v-dialog persistent max-width="500px" v-model="openScopeSelection">
            <ScopesSelect
                v-if="openScopeSelection"
                :wos="scopesWO"
                @print="print"
                @closeDialog="openScopeSelection = false"
            />
        </v-dialog>
        <v-dialog persistent max-width="250px" v-model="updatePackageParams">
            <UpdatePackageParams
                v-if="updatePackageParams"
                :projectId="project.id"
                :packageElement="selectedPackage"
                :packingListId="packingList.id"
                @closeDialog="closeUpdatePackageParams"
            />
        </v-dialog>
        <v-dialog persistent max-width="450px" v-model="updatePackageGrossMass">
            <UpdatePackageGrossMass
                v-if="updatePackageGrossMass"
                :projectId="project.id"
                :packageElement="selectedPackage"
                :packingListId="packingList.id"
                :onlySuggested="onlySuggested"
                @closeDialog="closeUpdatePackageGrossMass"
            />
        </v-dialog>
        <!--Error Alert-->
        <Errors />
        <!--Success Alert-->
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{
                position: 'absolute',
                right: '0px',
                bottom: '0px',
                zIndex: 1000,
            }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
    </div>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import { deleteFile } from '@/services/storage/deleteFile.js'
import { openFile } from '@/services/storage/openFile.js'
import { saveFile } from '@/services/storage/saveFile.js'
import _ from 'lodash'
import { printPackingPDF } from '@/components/Packing/packingPDF.js'
import { printPackagePDF } from './packagePDF'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import packingListRealtime from '@/components/Packing/mixins/packingListRealtime'
import { exportPackagesToExcel } from './PackagesXLSX.js'
import * as XLSX from 'xlsx/xlsx.mjs'

export default {
    name: 'PackingForm',
    mixins: [packingListRealtime],
    props: {
        project: {
            type: Object,
            required: true,
            default: () => {},
        },
        originalOrder: {
            type: Object,
            required: false,
            default: () => {},
        },
        onlySuggested: {
            type: Boolean,
            required: true,
            default: () => true,
        },
    },
    components: {
        Errors: () => import('@/components/Layout/Errors'),
        PackageForm: () => import('@/components/Packing/PackageForm.vue'),
        RejectedPackingListTable: () =>
            import('@/components/Packing/RejectedPackingListTable.vue'),
        DeliveryInfoForm: () =>
            import('@/components/Packing/DeliveryInfoForm.vue'),
        ScopesSelect: () => import('@/components/Packing/ScopesSelect.vue'),
        UpdatePackageParams: () =>
            import('@/components/Packing/UpdatePackageParams.vue'),
        UpdatePackageGrossMass: () =>
            import('@/components/Packing/UpdatePackageGrossMass.vue'),
        ImportPackageDialog: () =>
            import('@/components/Packing/ImportPackageDialog.vue'),
    },
    data: () => ({
        packingList: { packages: [] },
        companyId: JSON.parse(localStorage.getItem('company')),
        loading: false,
        packageForm: false,
        projectWorkOrders: [],
        projectBOMs: [],
        headers: [
            {
                text: 'WORK ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'TYPE',
                value: 'type',
                align: 'center',
                sortable: false,
            },

            {
                text: 'ITEM CODE',
                value: 'partNumber',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'FINISH & UC CODE',
                value: 'finishAndUCCode',
                align: 'center',
                sortable: false,
            },
            {
                text: 'UNIT MASS',
                value: 'mass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING QTY',
                value: 'packingQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING MASS',
                value: 'packingMass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'left',
                sortable: false,
            },
            {
                text: 'OC',
                value: 'oc',
                align: 'left',
                sortable: false,
            },
            {
                text: 'POSITION',
                value: 'costPosition',
                align: 'left',
                sortable: false,
            },
        ],
        suggestedHeaders: [
            {
                text: 'WORK ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'TYPE',
                value: 'type',
                align: 'center',
                sortable: false,
            },

            {
                text: 'ITEM CODE',
                value: 'partNumber',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'FINISH & UC CODE',
                value: 'finishAndUCCode',
                align: 'center',
                sortable: false,
            },
            {
                text: 'UNIT MASS',
                value: 'mass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'SUGGESTED QTY',
                value: 'suggestedQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING MASS',
                value: 'suggestedMass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'left',
                sortable: false,
            },
            {
                text: 'OC',
                value: 'oc',
                align: 'left',
                sortable: false,
            },
            {
                text: 'POSITION',
                value: 'costPosition',
                align: 'left',
                sortable: false,
            },
        ],
        workOrders: [],
        height: 350,
        valid: false,
        menu: false,
        menu2: false,
        selectedPackage: null,
        settingFinish: undefined,
        deleteDialog: false,
        importDialog: false,
        editionView: null,
        selectedWorkOrders: [],
        selectedBOMs: [],
        measureUnits: [],
        localAttachments: [],
        selectedFile: null,
        selectedProcess: { id: 'readyForPacking', name: 'Ready for packing' },
        packingProcess: { id: 'packing', name: 'Packing' },
        qualityProcess: { id: 'quality', name: 'Quality' },
        createdBy: null,
        users: [],
        rejectionTable: false,
        deliveryInfoDialog: false,
        deliveryInfoString: '',
        enableFocus: true,
        suggestedProcess: false,
        openScopeSelection: false,
        scopesWO: null,
        user: {},
        updatePackageParams: false,
        updatePackageGrossMass: false,
        projectPackingLists: [],
        schedulingOptions: false,
        packingItems: [],
        woData: {},
        allProjectWorOrders: [],
        loadingImportant: false,
        createPackingListPermission: false,
        planeationPermission: false,
        activateAlert: false,
        alertMessage: '',
        isProjectManager: false,
    }),
    computed: {
        // Block actions if packing list has blockActionsFromPMs and user doesn't have planning permissions
        blockActions() {
            return (
                !!this.packingList.blockActionsFromPMs &&
                !this.planeationPermission
            )
        },
        canToggleImportant() {
            // Only production planners can toggle important flag
            // And only if packingList exists (not during creation)
            if (!this.packingList.id) {
                return false
            }
            return (
                this.user.permissions &&
                this.user.permissions.includes('updateWOProcesses')
            )
        },
        minDate() {
            let baseDate

            if (this.originalOrder && this.originalOrder.createdOn) {
                // Editing existing packing list
                baseDate = moment.unix(
                    this.originalOrder.createdOn._seconds ||
                        this.originalOrder.createdOn.seconds
                )
            } else {
                // Creating new packing list
                baseDate = moment()
            }

            // Add 3 weeks to the base date
            const minDate = baseDate.add(3, 'weeks')

            // Return in YYYY-MM-DD format
            return minDate.format('YYYY-MM-DD')
        },
    },

    async mounted() {
        try {
            this.loading = true
            const {
                data: { users },
            } = await API.getLiteUsers()
            this.users = users
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            this.realtimeCurrentUserId = user.id

            // project manager role and collaborator in the project
            const isPmCollaborator =
                this.user.role &&
                this.user.role.toLowerCase() === 'project manager' &&
                this.project?.collaborators?.includes(this.user.id)
            // project manager role in the project
            const isPMofProject = this.user.id == this.project.projectManager

            this.isProjectManager = isPmCollaborator || isPMofProject

            // scheduling options permission
            this.schedulingOptions = this.user.permissions.includes(
                'updateWOProcesses'
            )

            // set create packing list permission
            this.createPackingListPermission = this.user.permissions.includes(
                'createPackingList'
            )

            // planeation permission
            this.planeationPermission = !!(
                this.createPackingListPermission || this.schedulingOptions
            )

            if (this.onlySuggested) {
                this.headers = this.suggestedHeaders
            } else if (this.originalOrder) {
                this.suggestedProcess = this.originalOrder.packages.some(
                    packageElement =>
                        packageElement.items.some(item => item.suggestedQty)
                )

                if (this.suggestedProcess) {
                    const suggestedHeader = {
                        text: 'SUGGESTED QTY',
                        value: 'suggestedQty',
                        align: 'right',
                        sortable: false,
                    }
                    this.headers.splice(4, 0, suggestedHeader)
                }
            }

            //get project packing lists
            this.projectPackingLists = await API.getPackingListsByProject({
                projectId: this.project.id,
            })

            if (this.originalOrder) {
                this.packingList = _.cloneDeep(this.originalOrder)
                this.createdBy = this.findUserName(this.packingList.createdBy)
                if (this.packingList.deliveryInformation) {
                    this.deliveryInfoString =
                        this.packingList.deliveryInformation.location +
                        ' - ' +
                        this.packingList.deliveryInformation.contact +
                        ' - ' +
                        this.packingList.deliveryInformation.phone
                }
                if (
                    this.packingList.packages &&
                    this.packingList.packages.length > 0
                ) {
                    this.getSelectedWorkOrdersAndBOMs()
                }
            }
            const {
                data: { settings },
            } = await API.getSettings()
            const settingIndex = settings.findIndex(u => u.name === 'Quotes')
            this.settingFinish = settings.find(s => s.name == 'FinishAndUCCode')
            if (settingIndex > -1) {
                this.measureUnits = settings[settingIndex].measureUnits
                    .filter(u => u.measure == 'length')
                    .map(unit => unit.abbr)
            }

            // this.projectWorkOrders = await API.getWorkOrdersByProject({
            //     projectId: this.project.id,
            //     includeClosed: false,
            // })

            this.allProjectWorOrders = await API.getWorkOrdersByProject({
                projectId: this.project.id,
                includeClosed: true,
            })

            this.projectWorkOrders = this.allProjectWorOrders.filter(
                w => w.status !== 'closed' && w.status !== 'cancelled'
            )

            this.allProjectWorOrders.forEach(workOrder => {
                // get wo data by code
                if (workOrder.code && !this.woData[workOrder.code]) {
                    this.woData[workOrder.code] = workOrder
                }
            })

            this.projectBOMs = await API.getBOMsChangelog({
                projectId: this.project.id,
                status: ['approved', 'closed'],
            })

            this.formatPackingItems()
            this.formatWorkOrders()
            this.formatBOMs()

            // Listener for all packing lists in the project
            this.setupProjectPackingListsListener()
            if (this.originalOrder) {
                this.setupCurrentPackingListListener()
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        formatWorkOrders(createProcess = false) {
            this.projectWorkOrders.forEach(workOrder => {
                const wo = this.formatWOItems(workOrder)
                //calculate available qty
                if (wo.items) {
                    wo.items.forEach(item => {
                        //filter the packing items for this item and wo
                        const packingRegisters = this.packingItems.filter(
                            packingItem =>
                                packingItem.workOrder == workOrder.code &&
                                packingItem.partNumber == item.partNumber
                        )
                        let totalPacked = 0
                        let totalSuggested = 0
                        let finalPacking = 0
                        packingRegisters.forEach(register => {
                            //packed
                            totalPacked += register.packingQty
                            //suggested
                            if (
                                !['referred', 'shipped', 'completed'].includes(
                                    register.status
                                )
                            ) {
                                totalSuggested += Math.max(
                                    register.packingQty,
                                    register.suggestedQty || 0
                                )
                            } else {
                                finalPacking += register.packingQty
                            }
                        })
                        const available = this.calculateAvailableQty(item)
                        item.availableSuggested =
                            item.quantity - totalSuggested - finalPacking
                        item.availableQty = available - totalPacked
                        item.packedQty = totalPacked
                        if (createProcess) {
                            item.packingQty = null
                            item.suggestedQty = null
                        }
                    })
                }
            })
        },
        formatBOMs(createProcess = false) {
            this.projectBOMs.forEach(bom => {
                //calculate available qty
                if (bom.items) {
                    bom.items.forEach(item => {
                        //filter the packing items for this item and bom
                        const packingRegisters = this.packingItems.filter(
                            packingItem =>
                                packingItem.bom == bom.code &&
                                packingItem.code == item.code
                        )
                        let totalPacked = 0
                        let totalSuggested = 0
                        let finalPacking = 0
                        packingRegisters.forEach(register => {
                            //packed
                            totalPacked += register.packingQty
                            //suggested
                            if (
                                !['referred', 'shipped', 'completed'].includes(
                                    register.status
                                )
                            ) {
                                totalSuggested += Math.max(
                                    register.packingQty,
                                    register.suggestedQty || 0
                                )
                            } else {
                                finalPacking += register.packingQty
                            }
                        })
                        const available = this.calculateAvailableBOMQty(item)
                        item.availableSuggested =
                            item.quantity - totalSuggested - finalPacking
                        item.availableQty = available - totalPacked
                        item.packedQty = totalPacked
                        if (createProcess) {
                            item.packingQty = null
                            item.suggestedQty = null
                            item.mass = null
                        }
                    })
                }
            })
        },
        formatWOItems(workOrder) {
            if (
                !(
                    (workOrder.status == 'closed' ||
                        workOrder.status == 'cancelled') &&
                    !workOrder.partNumbers
                )
            ) {
                delete workOrder.items
                workOrder.items = []
            }
            if (workOrder.partNumbers) {
                Object.keys(workOrder.partNumbers).forEach(partNumberId => {
                    let item = {
                        partNumberId,
                    }
                    Object.keys(workOrder.partNumbers[partNumberId]).forEach(
                        key => {
                            if (key == 'assemblyItems') {
                                let assemblyItems = []
                                Object.keys(
                                    workOrder.partNumbers[partNumberId][key]
                                ).forEach(aiKey => {
                                    assemblyItems.push({
                                        partNumberId: aiKey,
                                        ...workOrder.partNumbers[partNumberId][
                                            key
                                        ][aiKey],
                                    })
                                })
                                item[key] = assemblyItems
                            } else {
                                item[key] =
                                    workOrder.partNumbers[partNumberId][key]
                            }
                        }
                    )
                    workOrder.items.push(item)
                })
            }
            return workOrder
        },
        close() {
            this.$emit('close')
        },
        async toggleImportant() {
            try {
                this.loadingImportant = true
                const newImportantValue = !this.packingList.important
                await API.updatePackingList({
                    projectId: this.project.id,
                    packingListId: this.packingList.id,
                    important: newImportantValue,
                })
                // Update local state
                this.packingList.important = newImportantValue
                this.$emit('replacePackingList', this.packingList)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loadingImportant = false
            }
        },
        replaceItem(item, option) {
            if (item.finishAndUCCode) {
                if (this.settingFinish) {
                    let dataFinishAndUCCode = this.settingFinish.finishes.find(
                        e => e.id == item.finishAndUCCode
                    )
                    if (option == 'description') {
                        return (
                            dataFinishAndUCCode &&
                            dataFinishAndUCCode.description
                        )
                    } else if (option == 'code') {
                        return dataFinishAndUCCode && dataFinishAndUCCode.name
                    }
                }
            }
        },
        async print(scopes) {
            try {
                this.loading = true
                this.packingList.scopes = scopes
                await printPackingPDF(
                    this.packingList,
                    this.project,
                    this.onlySuggested
                )
                this.openScopeSelection = false
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async printPackage(packageElement, index) {
            try {
                this.loading = true
                await printPackagePDF(
                    this.packingList,
                    this.project,
                    packageElement,
                    index,
                    this.onlySuggested
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async createPackingList(persistentForm = false) {
            try {
                this.loading = true
                let pl = {
                    ...this.packingList,
                    totalMass: this.netMass(),
                    projectId: this.project.id,
                    projectManager: this.project.projectManager,
                    notes: this.packingList.notes || '',
                }
                pl.useSuggested = this.onlySuggested
                const newPackingList = await API.createPackingList(pl)
                // upload attachments
                if (this.localAttachments && this.localAttachments.length > 0) {
                    for (const file of this.localAttachments) {
                        await saveFile(
                            file,
                            `${this.companyId}/projects/${this.project.id}/packingLists`
                        )
                    }
                }
                // reasign data to the packing list
                this.packingList = newPackingList

                // Show success message
                this.alertMessage = `Packing List ${newPackingList.code} Successfully Created`
                this.activateAlert = true

                setTimeout(() => {
                    this.activateAlert = false
                }, 5000)

                this.$emit('addPackingList', newPackingList, persistentForm)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async updatePackingList(params) {
            try {
                this.menu = false
                if (this.originalOrder) {
                    await API.updatePackingList({
                        projectId: this.packingList.projectId,
                        packingListId: this.packingList.id,
                        ...params,
                    })
                    this.$emit('replacePackingList', this.packingList)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async updateBlockActionsFromPms(params) {
            try {
                this.menu = false
                if (this.originalOrder) {
                    const updatedPackingList = await API.updateBlockActionsFromPms(
                        {
                            projectId: this.packingList.projectId,
                            packingListId: this.packingList.id,
                            ...params,
                        }
                    )
                    // Update local pl
                    this.packingList.blockActionsFromPMs =
                        updatedPackingList.blockActionsFromPMs
                    this.$emit('replacePackingList', this.packingList)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        totalItemsQty() {
            if (this.onlySuggested) {
                return this.packingList.packages.reduce(
                    (accumulator, packageInfo) =>
                        accumulator +
                        packageInfo.items.reduce(
                            (itemAccumulator, item) =>
                                itemAccumulator + Number(item.suggestedQty),
                            0
                        ),
                    0
                )
            } else {
                return this.packingList.packages.reduce(
                    (accumulator, packageInfo) =>
                        accumulator +
                        packageInfo.items.reduce(
                            (itemAccumulator, item) =>
                                itemAccumulator + Number(item.packingQty),
                            0
                        ),
                    0
                )
            }
        },
        netMass() {
            if (this.onlySuggested) {
                return this.packingList.packages.reduce(
                    (accumulator, packageInfo) =>
                        accumulator +
                        packageInfo.items.reduce(
                            (itemAccumulator, item) =>
                                itemAccumulator +
                                Number(item.suggestedQty) * item.mass,
                            0
                        ),
                    0
                )
            } else {
                return this.packingList.packages.reduce(
                    (accumulator, packageInfo) =>
                        accumulator +
                        packageInfo.items.reduce(
                            (itemAccumulator, item) =>
                                itemAccumulator + Number(item.packingMass),
                            0
                        ),
                    0
                )
            }
        },
        grossMass() {
            const netMass = this.netMass()
            const cratesMass = this.packingList.packages.reduce(
                (accumulator, packageInfo) =>
                    accumulator + (+packageInfo.packageDimensions.mass || 0),

                0
            )
            return netMass + cratesMass
        },
        closePackageForm() {
            this.selectedPackage = null
            this.editionView = null
            this.packageForm = false
        },
        openCreationDialog() {
            this.formatPackingItems()
            this.formatWorkOrders(true)
            this.formatBOMs(true)
            this.editionView = false
            this.packageForm = true
        },
        openImportDialog() {
            this.importDialog = true
        },
        closeImportDialog() {
            this.importDialog = false
        },
        handleImport({ packingList, createdNewPackingList }) {
            try {
                // Parse JSON fields if needed
                if (typeof packingList.packages === 'string') {
                    packingList.packages = JSON.parse(packingList.packages)
                }
                if (typeof packingList.selectedWorkOrders === 'string') {
                    packingList.selectedWorkOrders = JSON.parse(
                        packingList.selectedWorkOrders
                    )
                }
                if (typeof packingList.reservedWorkOrders === 'string') {
                    packingList.reservedWorkOrders = JSON.parse(
                        packingList.reservedWorkOrders
                    )
                }
                if (typeof packingList.selectedBOMs === 'string') {
                    packingList.selectedBOMs = JSON.parse(
                        packingList.selectedBOMs
                    )
                }

                // Update packing list
                this.packingList = packingList

                // Update computed values
                this.getSelectedWorkOrdersAndBOMs()
                this.formatPackingItems()
                this.formatWorkOrders()
                this.formatBOMs()

                // Emit to update parent component
                if (createdNewPackingList) {
                    // Show success message
                    this.alertMessage = `Packing List ${this.packingList.code} Successfully Created`
                    this.activateAlert = true

                    setTimeout(() => {
                        this.activateAlert = false
                    }, 5000)

                    this.$emit('addPackingList', this.packingList, true)
                } else {
                    this.$emit('replacePackingList', this.packingList)
                }
                this.closeImportDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message || 'Error importing packages',
                })
            }
        },
        closeDeleteDialog() {
            this.selectedPackage = null
            this.deleteDialog = false
        },
        openDeleteDialog(packageData) {
            this.selectedPackage = _.cloneDeep(packageData)
            this.deleteDialog = true
        },
        openEditDialog(packageData) {
            this.formatPackingItems()
            this.formatWorkOrders()
            this.formatBOMs()
            this.editionView = true
            this.selectedPackage = packageData
            this.packageForm = true
        },
        addPackage({ newPackage, packages, reservedWorkOrders }) {
            if (packages && Array.isArray(packages)) {
                this.packingList.packages = _.cloneDeep(packages)
            } else if (newPackage) {
                const exists = this.packingList.packages.some(
                    p => p.id === newPackage.id
                )
                if (!exists) {
                    this.packingList.packages.push(newPackage)
                }
            }
            this.packingList.totalMass = this.netMass()
            if (reservedWorkOrders) {
                this.packingList.reservedWorkOrders = reservedWorkOrders
            }
            this.closePackageForm()
            this.getSelectedWorkOrdersAndBOMs()
            this.formatPackingItems()
            this.formatWorkOrders()
            this.formatBOMs()
            // create packing list if it is the first package and there is no packing list id.
            if (this.packingList.packages.length == 1 && !this.packingList.id) {
                this.createPackingList(true)
            }
            this.$emit('replacePackingList', this.packingList)
        },
        async deletePackage() {
            try {
                this.loading = true
                if (this.packingList.id) {
                    await API.deletePackage({
                        projectId: this.packingList.projectId,
                        packingListId: this.packingList.id,
                        packageId: this.selectedPackage.id,
                    })
                }

                const index = this.packingList.packages.findIndex(
                    p => p.id == this.selectedPackage.id
                )
                if (index > -1) {
                    this.packingList.packages.splice(index, 1)
                }

                this.packingList.totalMass = this.netMass()
                this.$emit('replacePackingList', this.packingList)
                this.getSelectedWorkOrdersAndBOMs()
                this.formatPackingItems()
                this.formatWorkOrders()
                this.formatBOMs()
                this.closeDeleteDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        replacePackage({ packageData }) {
            const index = this.packingList.packages.findIndex(
                p => p.id == this.selectedPackage.id
            )
            this.packingList.packages.splice(index, 1, packageData)
            this.recalculateBalances()
            this.closePackageForm()
            let totalMass = 0
            if (
                this.packingList.packages &&
                this.packingList.packages.length > 0
            ) {
                this.packingList.packages.forEach(pck => {
                    totalMass += pck.items.reduce(
                        (accum, item) => accum + Number(item.packingMass || 0),
                        0
                    )
                })
            }
            this.packingList.totalMass = totalMass
            this.$emit('replacePackingList', this.packingList)
        },
        workOrderTotalPacking(packageElement) {
            let total = 0
            packageElement.items.forEach(item => {
                if (this.onlySuggested) {
                    total += Number(item.suggestedQty)
                } else {
                    total += Number(item.packingQty)
                }
            })
            return total
        },
        workOrderTotalSuggested(packageElement) {
            return packageElement.items.reduce(
                (itemAccumulator, item) =>
                    itemAccumulator + Number(item.suggestedQty),
                0
            )
        },
        workOrderTotalMass(packageElement) {
            let mass = 0
            packageElement.items.forEach(item => {
                if (this.onlySuggested) {
                    if (item.mass) {
                        mass += Number(item.suggestedQty) * Number(item.mass)
                    }
                } else {
                    if (item.mass) {
                        mass += Number(item.packingQty) * Number(item.mass)
                    }
                }
            })
            return mass
        },
        getSelectedWorkOrdersAndBOMs() {
            // Only allow editing notes if packing list notes are empty or null
            const canEditNotes =
                this.packingList.notes == '' || this.packingList.notes == null
            if (canEditNotes) {
                this.packingList.notes = ''
            }

            // Process Work Orders
            const workOrdersSet = new Set()
            this.packingList.packages.forEach(pkg => {
                pkg.items.forEach(item => {
                    if (item.workOrder != undefined) {
                        workOrdersSet.add(item.workOrder)
                    }
                })
            })

            const toWorkOrderList = value => {
                if (value == null) return []
                if (Array.isArray(value)) return value
                if (typeof value === 'string') {
                    try {
                        const parsed = JSON.parse(value)
                        return Array.isArray(parsed) ? parsed : []
                    } catch {
                        return []
                    }
                }
                return []
            }
            const reservedWorkOrders = [
                ...toWorkOrderList(this.packingList?.reservedWorkOrders),
                ...toWorkOrderList(this.packingList?.selectedWorkOrders),
            ]
            if (reservedWorkOrders.length > 0) {
                reservedWorkOrders.forEach(wo => {
                    if (wo && wo.code) {
                        workOrdersSet.add(wo.code)
                    }
                })
            }

            const workOrdersArray = Array.from(workOrdersSet)
            this.packingList.selectedWorkOrders = workOrdersArray.map(
                (code, index) => {
                    const workOrder = this.projectWorkOrders.find(
                        wo => wo.code === code
                    )
                    if (canEditNotes && workOrder && workOrder.notes) {
                        this.packingList.notes += workOrder.notes
                        if (index < workOrdersArray.length - 1) {
                            this.packingList.notes += ' - '
                        }
                    }
                    return workOrder
                        ? { code, notes: workOrder.notes }
                        : { code, notes: '' }
                }
            )

            // Process BOMs
            const bomsSet = new Set()
            this.packingList.packages.forEach(pkg => {
                pkg.items.forEach(item => {
                    if (item.bom != undefined) {
                        bomsSet.add(item.bom)
                    }
                })
            })
            const bomsArray = Array.from(bomsSet)
            this.packingList.selectedBOMs = bomsArray.map((code, index) => {
                const bom = this.projectBOMs.find(bom => bom.code === code)
                if (canEditNotes && bom && bom.notes) {
                    // Add separator if there are already work order notes
                    if (
                        this.packingList.notes &&
                        this.packingList.notes.length > 0
                    ) {
                        this.packingList.notes += ' - '
                    }
                    this.packingList.notes += bom.notes
                    if (index < bomsArray.length - 1) {
                        this.packingList.notes += ' - '
                    }
                }
                return bom ? { code, notes: bom.notes } : { code, notes: '' }
            })

            return {
                selectedWorkOrders: this.packingList.selectedWorkOrders,
                selectedBOMs: this.packingList.selectedBOMs,
            }
        },
        onButtonClick() {
            this.$refs.uploader2.click()
        },
        async onFileChanged(e) {
            this.selectedFile = e.target.files[0]
            await this.saveFile(this.selectedFile)
        },
        async saveFile(file) {
            try {
                this.loading = true
                if (!this.packingList.status) {
                    this.localAttachments.push(file)
                    if (!this.packingList.attachments) {
                        this.packingList.attachments = [file.name]
                    } else {
                        this.packingList.attachments.push(file.name)
                    }
                    return null
                }
                await saveFile(
                    file,
                    `${this.companyId}/projects/${this.packingList.projectId}/packingLists`
                )
                if (!this.packingList.attachments) {
                    this.packingList.attachments = [file.name]
                } else {
                    this.packingList.attachments.push(file.name)
                }
                await API.packingListAttachments({
                    id: this.packingList.id,
                    projectId: this.packingList.projectId,
                    files: this.packingList.attachments,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async openFileAttached(file) {
            try {
                this.loading = true
                if (!this.packingList.status) {
                    const localFile = this.localAttachments.find(
                        f => f.name == file
                    )
                    if (localFile) {
                        const url = URL.createObjectURL(localFile)
                        window.open(url, '_blank')
                    }
                    return null
                }
                await openFile(
                    file,
                    `${this.companyId}/projects/${this.packingList.projectId}/packingLists`
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async deleteFile(file) {
            try {
                this.loading = true
                if (!this.packingList.status) {
                    this.localAttachments = this.localAttachments.filter(
                        f => f != file
                    )
                    this.packingList.attachments = this.packingList.attachments.filter(
                        f => f != file
                    )
                    return null
                }
                await deleteFile(
                    file,
                    `${this.companyId}/projects/${this.packingList.projectId}/packingLists`
                )
                this.packingList.attachments = this.packingList.attachments.filter(
                    f => f != file
                )
                await API.packingListAttachments({
                    id: this.packingList.id,
                    projectId: this.packingList.projectId,
                    files: this.packingList.attachments,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        buttonActions() {
            if (!this.originalOrder) {
                this.createPackingList()
            } else {
                this.markAsPending()
            }
        },
        calculateAvailableQty(item) {
            let availableQty = 0

            if (item.production) {
                item.production.forEach(production => {
                    if (production.process.id == this.selectedProcess.id) {
                        availableQty += production.qty
                    }
                })
            }
            if (item.deliveries) {
                let received = item.deliveries.filter(
                    register =>
                        register.nextProcess.id == this.packingProcess.id &&
                        register.receivedBy
                )
                const totalReceived = received.reduce(
                    (total, register) => total + register.qty,
                    0
                )

                let released = 0

                if (item.processes.includes(this.qualityProcess.id)) {
                    if (item.quality) {
                        released = item.quality.reduce(
                            (total, q) => total + (q.releaseQty || 0),
                            0
                        )
                    }
                } else {
                    released = totalReceived
                }
                availableQty += Math.min(totalReceived, released)
            }
            return availableQty
        },
        calculateAvailableBOMQty(item) {
            let availableQty = 0
            if (item.deliveries) {
                item.deliveries.forEach(delivery => {
                    availableQty += Number(delivery.qty || 0)
                })
            }
            return availableQty
        },
        findUserName(id) {
            let userName = ''
            const user = this.users.find(u => u.id == id)
            if (user) {
                userName = user.name
            }
            return userName
        },
        async markAsPending() {
            try {
                this.loading = true
                const packingList = await API.markPLAspending({
                    projectId: this.project.id,
                    packingListId: this.originalOrder.id,
                })
                this.$emit('replacePackingList', packingList)
                this.close()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        setDeliveryInfo(deliveryInfo) {
            this.packingList.deliveryInformation = deliveryInfo
            this.deliveryInfoString =
                deliveryInfo.location +
                ' - ' +
                deliveryInfo.contact +
                ' - ' +
                deliveryInfo.phone
            this.$emit('replacePackingList', this.packingList)
        },
        openDeliveryInfoDialog() {
            if (this.enableFocus) {
                this.deliveryInfoDialog = true
                this.$nextTick(() => {
                    this.enableFocus = false
                    this.$refs.deliveryInfoInput.blur()
                })
            }
        },
        closeDeliveryInfoDialog() {
            this.enableFocus = false
            this.deliveryInfoDialog = false
        },
        resetFocus() {
            this.enableFocus = true
        },
        qtyValidation() {
            const validation = this.packingList.packages.every(packageElement =>
                packageElement.items.some(
                    item => item.packingQty && item.packingQty > 0
                )
            )
            return !validation
        },
        missingReservedWorkOrders() {
            const reserved = this.packingList.reservedWorkOrders
            if (!reserved || reserved.length === 0) return []
            const selectedCodes = new Set(
                (this.packingList.selectedWorkOrders || []).map(wo => wo.code)
            )
            return reserved
                .map(wo => wo.code)
                .filter(code => !selectedCodes.has(code))
        },
        reservedWorkOrdersMismatch() {
            return this.missingReservedWorkOrders().length > 0
        },
        selectScopes() {
            let wos = []
            this.packingList.selectedWorkOrders?.forEach(wo => {
                const foundWO = this.projectWorkOrders.find(
                    w => w.code == wo.code
                )
                if (foundWO) {
                    wos.push(foundWO)
                }
            })
            this.scopesWO = wos
            this.openScopeSelection = true
        },
        openUpdatePackageGrossMass(packageElement) {
            this.updatePackageGrossMass = true
            this.selectedPackage = _.cloneDeep(packageElement)
        },
        openUpdatePackageParams(packageElement) {
            this.updatePackageParams = true
            this.selectedPackage = _.cloneDeep(packageElement)
        },
        closeUpdatePackageParams(updatedParams) {
            //update params in the package
            if (Object.keys(updatedParams).length > 0) {
                const index = this.packingList.packages.findIndex(
                    p => p.id == this.selectedPackage.id
                )
                if (index > -1) {
                    this.packingList.packages[index] = {
                        ...this.packingList.packages[index],
                        ...updatedParams,
                    }
                    this.$emit('replacePackingList', this.packingList)
                }
            }
            this.updatePackageParams = false
            this.selectedPackage = {}
        },
        closeUpdatePackageGrossMass(updatedData) {
            if (Object.keys(updatedData).length > 0) {
                const index = this.packingList.packages.findIndex(
                    p => p.id == this.selectedPackage.id
                )
                if (index > -1) {
                    const newCrateMass =
                        updatedData.packageDimensions?.mass !== undefined
                            ? updatedData.packageDimensions.mass
                            : this.packingList.packages[index].packageDimensions
                                  .mass
                    this.$set(
                        this.packingList.packages[index].packageDimensions,
                        'mass',
                        newCrateMass
                    )

                    this.$emit('replacePackingList', this.packingList)
                }
            }
            this.updatePackageGrossMass = false
            this.selectedPackage = {}
        },
        formatPackingItems() {
            this.packingItems = []
            this.projectPackingLists.forEach(pl => {
                if (pl.packages && pl.code != this.packingList.code) {
                    pl.packages.forEach(packageElement => {
                        if (packageElement.items) {
                            packageElement.items.forEach(item => {
                                let formattedItem = {
                                    suggestedQty: +item.suggestedQty || 0,
                                    packingQty: +item.packingQty || 0,
                                    status: pl.status,
                                }
                                if (item.workOrder) {
                                    formattedItem = {
                                        partNumber: item.partNumber,
                                        partNumberId: item.partNumberId,
                                        workOrder: item.workOrder,
                                        ...formattedItem,
                                    }
                                } else if (item.bom) {
                                    formattedItem = {
                                        code: item.code,
                                        bom: item.bom,
                                        ...formattedItem,
                                    }
                                }
                                this.packingItems.push(formattedItem)
                            })
                        }
                    })
                }
            })
            //add the current pl items
            if (this.packingList && this.packingList.packages) {
                this.packingList.packages.forEach(packageElement => {
                    if (packageElement.items) {
                        packageElement.items.forEach(item => {
                            let formattedItem = {
                                suggestedQty: +item.suggestedQty || 0,
                                packingQty: +item.packingQty || 0,
                                status: this.packingList.status,
                            }
                            if (item.workOrder) {
                                formattedItem = {
                                    partNumber: item.partNumber,
                                    partNumberId: item.partNumberId,
                                    workOrder: item.workOrder,
                                    ...formattedItem,
                                }
                            } else if (item.bom) {
                                formattedItem = {
                                    code: item.code,
                                    bom: item.bom,
                                    ...formattedItem,
                                }
                            }
                            this.packingItems.push(formattedItem)
                        })
                        // sort packages items in the order of the partNumbers
                        if (this.onlySuggested) {
                            packageElement.items.sort((a, b) => {
                                const numA =
                                    parseInt(a.partNumber?.slice(-4), 10) || 0
                                const numB =
                                    parseInt(b.partNumber?.slice(-4), 10) || 0
                                return numA - numB
                            })
                        }
                    }
                })
            }
        },

        async generateTempContainer() {
            const code = uuidv4().substring(0, 5)
            const container = `TEMP-${code}`
            let auxPL = _.cloneDeep(this.packingList)
            auxPL.container = container

            this.packingList = _.cloneDeep(auxPL)
            await this.updatePackingList({
                container: this.packingList.container,
            })
        },
        async generatePackagesXLSX() {
            try {
                this.loading = true
                // Determine which headers to use
                const currentHeaders = this.onlySuggested
                    ? this.suggestedHeaders
                    : this.headers

                await exportPackagesToExcel({
                    packingList: this.packingList,
                    headers: currentHeaders,
                    onlySuggested: this.onlySuggested,
                    settingFinish: this.settingFinish,
                    woData: this.woData,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        downloadCurrentPL() {
            if (
                !this.packingList ||
                !this.packingList.packages ||
                !this.packingList.id
            ) {
                return
            }

            const requiredColumns = [
                'PACKAGE',
                'WO',
                'PART NUMBER',
                'QUANTITY',
                'NOTES',
                'W',
                'H',
                'L',
                'UNIT',
                'CRATE MASS',
            ]

            const data = [requiredColumns]

            this.packingList.packages.forEach(
                (packageElement, packageIndex) => {
                    if (
                        !packageElement.items ||
                        packageElement.items.length === 0
                    ) {
                        return
                    }

                    const packageNumber = packageIndex + 1
                    const dimensions = packageElement.packageDimensions || {}

                    packageElement.items.forEach(item => {
                        const row = [
                            packageNumber, // PACKAGE
                            item.workOrder || '', // WO
                            item.partNumber || '', // PART NUMBER
                            item.suggestedQty || 0, // QUANTITY
                            item.notes || '', // NOTES
                            dimensions.width || 0, // W
                            dimensions.height || 0, // H
                            dimensions.depth || 0, // L
                            dimensions.units || '', // UNIT
                            dimensions.mass || 0, // CRATE MASS
                        ]
                        data.push(row)
                    })
                }
            )

            if (data.length === 1) {
                // Only headers, no data
                return
            }

            const ws = XLSX.utils.aoa_to_sheet(data)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'Packages Export')
            XLSX.writeFile(
                wb,
                `${this.packingList.code || 'packing_list'}_export.xlsx`
            )
        },
        buttonValidations() {
            let validation
            if (this.schedulingOptions) {
                validation =
                    !this.valid ||
                    (!this.packingList.scheduledFor &&
                        !this.packingList.requestedShipmentDate)
            } else {
                validation =
                    !this.valid ||
                    this.packingList.packages.length == 0 ||
                    !this.packingList.requestedShipmentDate
            }
            return validation
        },
        recalculateBalances() {
            this.formatPackingItems()
            this.formatWorkOrders()
            this.formatBOMs()
            this.getSelectedWorkOrdersAndBOMs()
        },
    },
}
</script>

<style scoped>
#virtual-scroll-table {
    width: 100%;
    overflow: auto;
}

.package-row-highlight {
    animation: package-highlight-pulse 4s ease-out;
}

@keyframes package-highlight-pulse {
    0% {
        background-color: rgba(33, 150, 243, 0.25);
    }
    100% {
        background-color: transparent;
    }
}
</style>
